import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeleteProfile, useProfileCompletionDetails, useProfileByUsernameOrId } from '@/hooks/useProfile';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { learningPlanApi } from '@/services/learningManagementApi';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Edit, Trash2, FileText, User, ShieldCheck, FileSignature, UserCheck, Mail, Bell, Cookie, BarChart3, Share2, Phone, MapPin, Calendar, Clock, Globe, Palette, Target, Activity, CheckCircle2, BookOpen } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import { UserProfileStatus } from '@/types/userProfile';
import { BioEditor } from '@/components/profiles/BioEditor';
import { SkillManager } from '@/components/profiles/SkillManager';
import { OccupationManager } from '@/components/profiles/OccupationManager';
import { WinsAndPainPointsCard } from '@/components/profiles/WinsAndPainPointsCard';
import { ProfileAvatar } from '@/components/profiles/ProfileAvatar';
import { ProfilePictureUpload } from '@/components/profiles/ProfilePictureUpload';
import { ProfileCompletionDetails } from '@/components/profiles/ProfileCompletionDetails';
import { ProfileUserRolesCard } from '@/components/admin/ProfileUserRolesCard';
import { useProfilePictureUrl } from '@/hooks/useProfilePictureUrl';
import type { UploadProfilePictureResult } from '@/services/profilePictureService';

export const ProfileDetailPage: React.FC = () => {
  const { userName } = useParams<{ userName: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAdminAuth();
  const { data: profile, isLoading: isLoadingProfile, error: profileError, refetch } = useProfileByUsernameOrId(userName);
  const profileEmail = profile?.email;
  const userId = profile?.userId;
  const userIdentifier = profile?.email ?? userId;

  const { data: learningPlan } = useQuery({
    queryKey: ['learning-path', 'user', userIdentifier],
    queryFn: () => learningPlanApi.getByUser(userIdentifier!),
    enabled: !!userIdentifier,
    staleTime: 2 * 60 * 1000,
  });
  
  const { data: completionDetails, isLoading: isLoadingCompletion } = useProfileCompletionDetails(userId, !!userId);
  const deleteProfile = useDeleteProfile();
  const { url: profilePictureUrl } = useProfilePictureUrl(profile?.profilePictureId);

  const safeFormatDistance = (dateStr: string | undefined | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    try {
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (_e) {
      return '—';
    }
  };

  const safeLocaleString = (dateStr: string | undefined | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const canEdit = hasPermission('edit', 'userProfile');
  const canDelete = hasPermission('delete', 'userProfile');
  const displayName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || profile?.userName || profile?.email || profile?.userId || 'User';

  const handleProfilePictureSuccess = (_result: UploadProfilePictureResult) => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['profile-picture-url'] });
    queryClient.invalidateQueries({ queryKey: ['profiles', 'detail'] });
  };

  const handleDelete = async () => {
    if (!userId || !window.confirm('Are you sure you want to delete this profile? This action cannot be undone.')) return;

    try {
      await deleteProfile.mutateAsync(userId);
      navigate('/admin/profiles');
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const getStatusBadge = (status: UserProfileStatus) => {
    const variants: Record<UserProfileStatus, { className: string }> = {
      [UserProfileStatus.ACTIVE]: { className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      [UserProfileStatus.INACTIVE]: { className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
      [UserProfileStatus.SUSPENDED]: { className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
      [UserProfileStatus.PENDING]: { className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
      [UserProfileStatus.DRAFT]: { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
    };
    const config = variants[status] || variants[UserProfileStatus.INACTIVE];
    return (
      <Badge className={config.className}>
        {status}
      </Badge>
    );
  };

  if (isLoadingProfile) {
    return <LoadingState message="Loading profile..." />;
  }

  if (profileError || !profile || !profile.userId) {
    const error = profileError || (profile && !profile.userId ? new Error('Profile data is incomplete (missing userId)') : new Error('Profile not found'));
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/profiles">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <User className="h-8 w-8" />
              {profile.userName || profile.email || profile.userId}
            </h1>
            <p className="text-muted-foreground mt-2">User Profile Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {profile.email && (
            <Button 
              variant="default" 
              className="bg-gradient-to-r from-primary to-violet-600 hover:shadow-lg hover:shadow-primary/20 text-white"
              onClick={() => navigate(`/learning-path/${encodeURIComponent(profile.email)}`)}
            >
              <BookOpen className="mr-2 h-4 w-4" />
              View Learning Path
            </Button>
          )}
          {canEdit && (
            <>
              <Button asChild>
                <Link to={`/admin/profiles/${userName}/edit`}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Profile
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to={`/admin/profiles/${userName}/bio/edit`}>
                  <FileText className="mr-2 h-4 w-4" />
                  Edit Bio
                </Link>
              </Button>
            </>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteProfile.isPending}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="completion">Completion</TabsTrigger>
          <TabsTrigger value="bio">Bio & Summary</TabsTrigger>
          <TabsTrigger value="skills">Skills & Occupations</TabsTrigger>
          <TabsTrigger value="activity">Activity History</TabsTrigger>
          <TabsTrigger value="compliance">Compliance & Consent</TabsTrigger>
          {profileEmail && <TabsTrigger value="roles">Identity & roles</TabsTrigger>}
        </TabsList>

        {/* Overview Tab — Persona card */}
        <TabsContent value="overview" className="space-y-6">
          {/* Persona hero card */}
          <Card className="overflow-hidden border-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-amber-500/10 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-amber-500/20 shadow-lg">
            <CardContent className="p-8">
              <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:gap-8">
                {canEdit ? (
                  <ProfilePictureUpload
                    userId={profile.userId}
                    currentFileUrl={profilePictureUrl}
                    displayName={displayName}
                    onSuccess={handleProfilePictureSuccess}
                    size="xl"
                    className="shrink-0"
                  />
                ) : (
                  <ProfileAvatar
                    src={profilePictureUrl}
                    displayName={displayName}
                    size="xl"
                    className="shrink-0"
                  />
                )}
                <div className="flex-1 text-center sm:text-left space-y-3 min-w-0">
                  <h2 className="text-2xl font-bold tracking-tight">
                    {[profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.userName || profile.email}
                  </h2>
                  {(profile.userName || profile.email) && (
                    <p className="text-muted-foreground flex items-center justify-center sm:justify-start gap-2">
                      <Mail className="h-4 w-4 shrink-0" />
                      {profile.userName && profile.userName !== profile.email ? `${profile.userName} · ` : ''}
                      {profile.email}
                      {profile.emailVerified && (
                        <Badge variant="outline" className="text-xs ml-1">Verified</Badge>
                      )}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                    {getStatusBadge(profile.status)}
                    {profile.isOnboardingCompleted ? (
                      <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">Onboarding done</Badge>
                    ) : (
                      <Badge variant="outline">Onboarding pending</Badge>
                    )}
                  </div>
                  <div className="pt-2">
                    <div className="flex items-center justify-between gap-4 mb-1">
                      <span className="text-sm font-medium text-muted-foreground">Profile completion</span>
                      <span className="text-sm font-medium">{profile.profileCompletionPercentage ?? 0}%</span>
                    </div>
                    <Progress value={profile.profileCompletionPercentage ?? 0} className="h-2" />
                  </div>
                  <div className="pt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    {profile.sessionId && <CopyableId id={profile.sessionId} label="Session ID" showLabel={false} codeClassName="text-xs" />}
                    {profile.externalRefId && <CopyableId id={profile.externalRefId} label="External ref" showLabel={false} codeClassName="text-xs" />}
                    <CopyableId id={profile.userId} label="User ID" showLabel={false} codeClassName="text-xs" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Learning Path Quick Access Card */}
          {profile.email && (
            <Card className="overflow-hidden border-0 bg-gradient-to-br from-primary/10 via-violet-500/5 to-fuchsia-500/10 dark:from-primary/20 dark:via-violet-500/10 dark:to-fuchsia-500/20 shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-violet-600 flex items-center justify-center shadow-lg shadow-primary/25 shrink-0">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <h3 className="text-xl font-bold">Personalized Learning Path</h3>
                      <p className="text-sm text-muted-foreground">
                        View the AI-generated learning journey designed specifically for this user. Explore modules, daily content, and track progress.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-primary to-violet-600 hover:shadow-xl hover:shadow-primary/20 text-white rounded-xl px-8 font-bold shrink-0"
                    onClick={() => navigate(`/learning-path/${encodeURIComponent(profile.email)}`)}
                  >
                    <BookOpen className="w-5 h-5 mr-2" />
                    View Learning Path
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Personal information grid */}
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal information
                </CardTitle>
                <CardDescription>Identity and contact details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {profile.email && (
                    <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                      <Mail className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</p>
                        <p className="text-sm font-medium break-all">{profile.email}</p>
                      </div>
                    </div>
                  )}
                  {profile.phoneNumber && (
                    <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                      <Phone className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Phone</p>
                        <p className="text-sm font-medium">
                          {(() => {
                            const cc = String(profile.phoneNumber!.countryCode ?? '').trim();
                            const prefix = cc && !cc.startsWith('+') ? '+' : '';
                            return [prefix + cc, profile.phoneNumber!.number].filter(Boolean).join(' ');
                          })()}
                        </p>
                        {profile.phoneNumber.verified && <Badge variant="outline" className="text-xs mt-1">Verified</Badge>}
                      </div>
                    </div>
                  )}
                  {profile.dateOfBirth && (
                    <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                      <Calendar className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Date of birth</p>
                        <p className="text-sm font-medium">{profile.dateOfBirth}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                    <User className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Gender</p>
                      <p className="text-sm font-medium">{profile.gender ?? '—'}</p>
                    </div>
                  </div>
                </div>
                {profile.userName && profile.userName !== profile.email && (
                  <div className="rounded-lg border bg-muted/20 p-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Username</p>
                    <p className="text-sm font-medium mt-0.5">{profile.userName}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location & Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Location & activity
                </CardTitle>
                <CardDescription>Where they are and when they were last active</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.location && (profile.location.country || profile.location.city || profile.location.timezone) ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                      <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                      <div className="min-w-0 text-sm">
                        {[profile.location.city, profile.location.state, profile.location.country].filter(Boolean).join(', ') || '—'}
                        {profile.location.postalCode && ` · ${profile.location.postalCode}`}
                      </div>
                    </div>
                    {profile.location.timezone && (
                      <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                        <Globe className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Timezone</p>
                          <p className="text-sm font-medium">{profile.location.timezone}</p>
                        </div>
                      </div>
                    )}
                    {profile.preferences?.preferredLanguage && (
                      <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                        <Globe className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Language</p>
                          <p className="text-sm font-medium">{profile.preferences.preferredLanguage}</p>
                        </div>
                      </div>
                    )}
                    {profile.preferences?.preferredTheme && (
                      <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                        <Palette className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Theme</p>
                          <p className="text-sm font-medium">{profile.preferences.preferredTheme}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">No location data</p>
                )}
                <div className="space-y-3 pt-2 border-t">
                  <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
                    <Clock className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Last active</p>
                      <p className="text-sm font-medium">
                        {safeFormatDistance(profile.lastActiveAt)}
                      </p>
                      {profile.lastActiveAt && !isNaN(new Date(profile.lastActiveAt).getTime()) && (
                        <p className="text-xs text-muted-foreground mt-0.5">{new Date(profile.lastActiveAt).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</p>
                      <p className="text-sm font-medium">{safeLocaleString(profile.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Updated</p>
                      <p className="text-sm font-medium">{safeLocaleString(profile.updatedAt)}</p>
                    </div>
                  </div>
                  {(profile.createdBy || profile.lastModifiedBy) && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {profile.createdBy && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Created by</p>
                          <p className="text-sm font-medium">{profile.createdBy}</p>
                        </div>
                      )}
                      {profile.lastModifiedBy && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Modified by</p>
                          <p className="text-sm font-medium">{profile.lastModifiedBy}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Completion Tab */}
        <TabsContent value="completion" className="space-y-6">
          {isLoadingCompletion ? (
            <LoadingState message="Loading completion details..." />
          ) : completionDetails ? (
            <ProfileCompletionDetails profileId={userId!} details={completionDetails} />
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground font-medium">Completion details unavailable</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Unable to load profile completion information.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Bio & Summary Tab */}
        <TabsContent value="bio" className="space-y-4">
          <BioEditor profileId={userId!} bio={profile.bio} readOnly={true} />
        </TabsContent>

        {/* Skills & Occupations Tab */}
        <TabsContent value="skills" className="space-y-6">
          <Card className="overflow-hidden border-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-amber-500/10 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-amber-500/20 shadow-lg">
            <CardContent className="p-8">
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-6">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-amber-500 text-white shadow-lg">
                  <Target className="h-7 w-7" />
                </div>
                <div className="text-center sm:text-left">
                  <h2 className="text-xl font-bold tracking-tight">Skills & Occupations</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage skills, occupations, wins, and pain points. Display names come from master data.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <SkillManager profileId={userId!} skills={profile.bio?.skills || []} />
          <OccupationManager profileId={userId!} occupations={profile.bio?.occupations || []} currentOccupation={profile.bio?.currentOccupation} />
          <WinsAndPainPointsCard
            winsLookingFor={profile.bio?.winsLookingFor}
            painPoints={profile.bio?.painPoints}
            learningPlan={learningPlan ?? undefined}
          />
        </TabsContent>

        {/* Activity History Tab */}
        <TabsContent value="activity" className="space-y-6">
          <Card className="overflow-hidden border-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-amber-500/10 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-amber-500/20 shadow-lg">
            <CardContent className="p-8">
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-6">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-amber-500 text-white shadow-lg">
                  <Activity className="h-7 w-7" />
                </div>
                <div className="text-center sm:text-left">
                  <h2 className="text-xl font-bold tracking-tight">Activity History</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    User activity tracking and history.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Activity className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground font-medium">Activity history coming soon</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                Logins, page views, and other events will appear here once the feature is available.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance & Consent Tab */}
        <TabsContent value="compliance" className="space-y-6">
          {!profile.gdprCompliance && !profile.termsAcceptance && !profile.userConsent ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <ShieldCheck className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground font-medium">No compliance data recorded</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                  GDPR, terms acceptance, and consent preferences will appear here once the user completes onboarding or updates their preferences.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
              {/* GDPR Compliance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ShieldCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    GDPR Compliance
                  </CardTitle>
                  <CardDescription>Data processing consent and privacy requests</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {profile.gdprCompliance ? (
                    <>
                      <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Data processing consent</span>
                          <Badge
                            variant={profile.gdprCompliance.dataProcessingConsent ? 'default' : 'secondary'}
                            className={profile.gdprCompliance.dataProcessingConsent ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                          >
                            {profile.gdprCompliance.dataProcessingConsent ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                        {profile.gdprCompliance.consentGivenAt && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Consent given</p>
                            <p className="text-sm mt-0.5">{safeLocaleString(profile.gdprCompliance.consentGivenAt)}</p>
                          </div>
                        )}
                        {profile.gdprCompliance.consentWithdrawnAt && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Consent withdrawn</p>
                            <p className="text-sm mt-0.5">{safeLocaleString(profile.gdprCompliance.consentWithdrawnAt)}</p>
                          </div>
                        )}
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {profile.gdprCompliance.dataExportRequested != null && (
                          <div className="flex items-center justify-between rounded-md border px-3 py-2">
                            <span className="text-sm">Data export requested</span>
                            <Badge variant={profile.gdprCompliance.dataExportRequested ? 'default' : 'outline'}>
                              {profile.gdprCompliance.dataExportRequested ? 'Yes' : 'No'}
                            </Badge>
                          </div>
                        )}
                        {profile.gdprCompliance.dataDeletionRequested != null && (
                          <div className="flex items-center justify-between rounded-md border px-3 py-2">
                            <span className="text-sm">Data deletion requested</span>
                            <Badge variant={profile.gdprCompliance.dataDeletionRequested ? 'default' : 'outline'}>
                              {profile.gdprCompliance.dataDeletionRequested ? 'Yes' : 'No'}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4">No GDPR compliance data</p>
                  )}
                </CardContent>
              </Card>

              {/* Terms Acceptance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileSignature className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    Terms Acceptance
                  </CardTitle>
                  <CardDescription>Terms of service and legal agreement</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {profile.termsAcceptance ? (
                    <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                      {profile.termsAcceptance.acceptedAt && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Accepted at</p>
                          <p className="text-sm mt-0.5">{safeLocaleString(profile.termsAcceptance.acceptedAt)}</p>
                        </div>
                      )}
                      {profile.termsAcceptance.termsVersion && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Terms version</p>
                          <p className="text-sm mt-0.5 font-mono">{profile.termsAcceptance.termsVersion}</p>
                        </div>
                      )}
                      {profile.termsAcceptance.acceptedIpAddress && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">IP address</p>
                          <p className="text-sm mt-0.5 font-mono">{profile.termsAcceptance.acceptedIpAddress}</p>
                        </div>
                      )}
                      {profile.termsAcceptance.acceptedUserAgent && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">User agent</p>
                          <p className="text-xs mt-0.5 text-muted-foreground break-all" title={String(profile.termsAcceptance.acceptedUserAgent)}>
                            {typeof profile.termsAcceptance.acceptedUserAgent === 'string' && profile.termsAcceptance.acceptedUserAgent.length > 80
                              ? `${profile.termsAcceptance.acceptedUserAgent.slice(0, 80)}…`
                              : String(profile.termsAcceptance.acceptedUserAgent)}
                          </p>
                        </div>
                      )}
                      {!profile.termsAcceptance.acceptedAt && !profile.termsAcceptance.termsVersion && !profile.termsAcceptance.acceptedIpAddress && !profile.termsAcceptance.acceptedUserAgent && (
                        <p className="text-sm text-muted-foreground">No details recorded</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4">No terms acceptance data</p>
                  )}
                </CardContent>
              </Card>

              {/* User Consent — full width */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <UserCheck className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                    User Consent Preferences
                  </CardTitle>
                  <CardDescription>Communication and data usage preferences</CardDescription>
                </CardHeader>
                <CardContent>
                  {profile.userConsent ? (
                    <div className="space-y-4">
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {[
                          { key: 'termsAccepted', label: 'Terms accepted', icon: FileSignature },
                          { key: 'privacyPolicyAccepted', label: 'Privacy policy', icon: ShieldCheck },
                          { key: 'marketingEmails', label: 'Marketing emails', icon: Mail },
                          { key: 'smsNotifications', label: 'SMS notifications', icon: Bell },
                          { key: 'pushNotifications', label: 'Push notifications', icon: Bell },
                          { key: 'dataAnalytics', label: 'Data analytics', icon: BarChart3 },
                          { key: 'thirdPartySharing', label: 'Third‑party sharing', icon: Share2 },
                          { key: 'cookieConsent', label: 'Cookie consent', icon: Cookie },
                        ].map(({ key, label, icon: Icon }) => {
                          const uc = profile.userConsent!;
                          const value = uc[key as keyof typeof uc];
                          if (typeof value !== 'boolean') return null;
                          return (
                            <div
                              key={key}
                              className="flex items-center justify-between rounded-lg border bg-muted/20 px-4 py-3 transition-colors hover:bg-muted/40"
                            >
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                                <span className="text-sm font-medium">{label}</span>
                              </div>
                              <Badge
                                variant={value ? 'default' : 'secondary'}
                                className={value ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                              >
                                {value ? 'Yes' : 'No'}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                      {(profile.userConsent.consentTimestamp || profile.userConsent.consentVersion) && (
                        <div className="mt-4 flex flex-wrap gap-6 rounded-lg border bg-muted/20 px-4 py-3">
                          {profile.userConsent.consentTimestamp && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Consent timestamp</p>
                              <p className="text-sm mt-0.5">{safeLocaleString(profile.userConsent.consentTimestamp)}</p>
                            </div>
                          )}
                          {profile.userConsent.consentVersion && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Version</p>
                              <p className="text-sm mt-0.5 font-mono">{profile.userConsent.consentVersion}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4">No user consent data</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Identity & roles — super admin can add/remove user roles */}
        {profileEmail && (
          <TabsContent value="roles" className="space-y-6">
            <ProfileUserRolesCard email={profileEmail} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ProfileDetailPage;







